// src/components/LayerPanel/CountyDetailFlyout.jsx
//
// Side-panel flyout opened from a series row's coverage chevron. Shows one
// card per AOI-touched county with a thumbnail (county + AOI + coverage
// overlay) and the precise numbers (county name, percent, sq mi).
//
// Positioning: top/height MEASURED from the LayerPanel element at runtime
// (via [data-layer-panel]) so it aligns with the panel regardless of
// layout, and slides in from the panel's right edge.
//
// Rendered into a portal at document.body (MapPage's <Panel> sets a
// transform, which would otherwise become the containing block for
// position:fixed and clip us).
//
// Thumbnail click → flyTo the AOI-within-county. Clicking a county's map
// thumbnail flies the main map to the bounding box of THAT county's
// AOI-clipped geometry (aoi_county_geom) — i.e. the user's actual work
// area in that county, not the whole county. We route through the
// existing useMapActions zoom handler (the same channel MapView registers
// for "move the map from elsewhere"), so no MapView change and no prop
// threading across the portal boundary. The flyout stays open so the user
// can tour multiple counties in sequence.
//
// Coverage source (counties + percentages):
//   - selectedAcquisitions non-empty → rollUpCounties(those)
//   - empty → series.counties (all-acquisitions rollup, discovery view)
// Geometry source (thumbnail shapes + flyTo bbox): aoiCounties by
// county_key, collection-level, identical across acquisitions.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fmtCoveragePct } from "../../layers/imagery/formatCountyCoverage";
import {
  bucketCounties,
  formatCoverageSummary,
} from "../../layers/imagery/coverageBuckets";
import { rollUpCounties } from "../../layers/imagery/groupFootprintsIntoSeries";
import { useMapActions } from "../../stores/useMapActions";
import CountyThumbnail from "./CountyThumbnail";

const FLYOUT_WIDTH = 480;
const PANEL_SELECTOR = "[data-layer-panel]";

// Compute [minLng, minLat, maxLng, maxLat] from a GeoJSON Polygon/
// MultiPolygon. Mirrors extractBounds() in groupFootprintsIntoSeries.js
// but returns the flat 4-tuple the useMapActions zoom handler expects
// (it does fitBounds([[minX,minY],[maxX,maxY]], …) internally).
//
// Returns null for missing/degenerate geometry so callers can no-op
// rather than fly the map to NaN.
function bboxFromGeoJSON(geom) {
  if (!geom) return null;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  const processCoord = (c) => {
    const x = c[0];
    const y = c[1];
    if (typeof x !== "number" || typeof y !== "number") return;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  const walk = (coords) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number") processCoord(coords);
    else coords.forEach(walk);
  };

  const geometry = geom.type === "Feature" ? geom.geometry : geom;
  if (!geometry?.coordinates) return null;

  walk(geometry.coordinates);

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return null;
  }
  return [minX, minY, maxX, maxY];
}

export default function CountyDetailFlyout({
  series,
  selectedAcquisitions,
  aoiCounties,
  onClose,
}) {
  const ref = useRef(null);

  // The map-move channel MapView registers via useMapActions. Calling
  // this with [minX,minY,maxX,maxY] triggers an animated fitBounds on the
  // main map (duration 600, maxZoom 18 — set in MapView's handler).
  const zoomToBounds = useMapActions((s) => s.zoomToBounds);
  
  const [panelRect, setPanelRect] = useState(null);
  const [entered, setEntered] = useState(false);

  useLayoutEffect(() => {
    function measure() {
      const el = document.querySelector(PANEL_SELECTOR);
      if (!el) {
        setPanelRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setPanelRect({ top: r.top, height: r.height, right: r.right });
    }
    measure();

    const el = document.querySelector(PANEL_SELECTOR);
    let ro;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    // Esc closes the flyout and takes the key, so Explore's compare view
    // stays on (useEscapeExitsCompare).
    function onKey(e) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      onClose?.();
    }
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [onClose]);

  const counties = useMemo(() => {
    if (Array.isArray(selectedAcquisitions) && selectedAcquisitions.length > 0) {
      return rollUpCounties(selectedAcquisitions) ?? [];
    }
    return series?.counties ?? [];
  }, [selectedAcquisitions, series]);

  if (!series) return null;

  const geomByKey = new Map();
  if (Array.isArray(aoiCounties)) {
    for (const ac of aoiCounties) geomByKey.set(ac.county_key, ac);
  }

  const summary = formatCoverageSummary(bucketCounties(counties));

  const scopeLabel =
    Array.isArray(selectedAcquisitions) && selectedAcquisitions.length > 0
      ? selectedAcquisitions.length === 1
        ? `Acquisition ${selectedAcquisitions[0].acquisition_date}`
        : `${selectedAcquisitions.length} selected acquisitions`
      : "All acquisitions (nothing selected)";

  // Fly the main map to a county's AOI-within-county bbox. No-op (with a
  // dev warning) if the geometry is missing/degenerate or the map zoom
  // channel isn't registered yet (map still loading). Flyout stays open.
  function flyToCounty(geom, countyKey) {
    if (typeof zoomToBounds !== "function") {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(
          "[CountyDetailFlyout] map zoom handler not ready; ignoring flyTo for",
          countyKey
        );
      }
      return;
    }
    const bbox = bboxFromGeoJSON(geom?.aoi_county_geom);
    if (!bbox) {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn(
          "[CountyDetailFlyout] no usable aoi_county_geom bbox for",
          countyKey
        );
      }
      return;
    }
    zoomToBounds(bbox);
  }

  const positionStyle = panelRect
    ? {
        top: panelRect.top,
        height: panelRect.height,
        left: panelRect.right,
        width: FLYOUT_WIDTH,
      }
    : {
        top: 0,
        height: "100vh",
        left: "calc(20rem + 50px)",
        width: FLYOUT_WIDTH,
      };

  const content = (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Coverage detail for ${series.display_name}`}
      className={[
        "fixed bg-bg-softer border-r border-slate-800",
        "shadow-xl shadow-black/40 flex flex-col z-40",
        "transition-transform duration-200 ease-out",
        entered ? "translate-x-0" : "-translate-x-3",
      ].join(" ")}
      style={{ ...positionStyle, opacity: entered ? 1 : 0 }}
    >
      {/* Header */}
      <div className="flex items-start gap-2 px-3 py-3 border-b border-slate-800">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-gray-200 truncate font-medium">
            {series.display_name}
          </div>
          <div className="text-[9px] text-fg-secondary mt-0.5 truncate">
            {scopeLabel}
          </div>
          {summary && (
            <div className="text-[10px] text-gray-400 mt-0.5 truncate">
              {summary}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close coverage detail"
          className="p-1 rounded text-fg-secondary hover:text-gray-200 hover:bg-slate-700 transition flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3">
        {counties.length === 0 ? (
          <div className="text-[10px] text-fg-secondary italic">
            No coverage in your AOI.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {counties.map((c) => (
              <CountyCard
                key={c.county_key}
                county={c}
                geom={geomByKey.get(c.county_key)}
                onFlyTo={flyToCounty}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function CountyCard({ county, geom, onFlyTo }) {
  const pct = fmtCoveragePct(county.aoi_coverage_ratio);
  const sqMiRaw =
    county.aoi_in_county_sq_miles ?? geom?.aoi_in_county_sq_miles ?? null;
  const sqMi = sqMiRaw != null ? Math.round(sqMiRaw) : null;

  if (!geom) {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        "[CountyDetailFlyout] no aoi_counties geom for",
        county.county_key
      );
    }
  }

  // Thumbnail is the click target (it reads as a map → clicking navigates).
  // Flies to the AOI-within-county; flyout stays open for touring.
  const canFly = !!geom;
  function handleThumbClick(e) {
    e.stopPropagation(); // don't bubble toward the flyout's outside-click
    if (!canFly) return;
    onFlyTo?.(geom, county.county_key);
  }
  function handleThumbKey(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleThumbClick(e);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div
        className={[
          "group relative rounded border border-slate-800 overflow-hidden bg-slate-900",
          canFly
            ? "cursor-pointer hover:border-accent-info/60 transition-colors"
            : "",
        ].join(" ")}
        onClick={handleThumbClick}
        onKeyDown={canFly ? handleThumbKey : undefined}
        role={canFly ? "button" : undefined}
        tabIndex={canFly ? 0 : undefined}
        aria-label={
          canFly ? `Fly to your work area in ${county.county}` : undefined
        }
        title={canFly ? `Fly to ${county.county}` : undefined}
      >
        {geom ? (
          <CountyThumbnail
            countyGeom={geom.county_geom}
            aoiCountyGeom={geom.aoi_county_geom}
            coverageRatio={county.aoi_coverage_ratio}
          />
        ) : (
          <div className="w-[140px] h-[140px] flex items-center justify-center text-[9px] text-fg-secondary">
            no geom
          </div>
        )}
        {canFly && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center
                       opacity-0 group-hover:opacity-100 transition-opacity bg-black/20"
          >
            <span className="text-[9px] font-mono uppercase tracking-wider text-accent-info bg-slate-900/80 px-1.5 py-0.5 rounded">
              Fly to
            </span>
          </div>
        )}
      </div>
      <div className="text-[11px] text-gray-200 truncate" title={county.county}>
        {county.county}
      </div>
      {pct && (
        <div className="text-[10px] text-gray-400 tabular-nums">{pct}</div>
      )}
      {sqMi != null && (
        <div className="text-[9px] text-fg-secondary">
          {sqMi.toLocaleString()} sq mi of your AOI
        </div>
      )}
    </div>
  );
}

